<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- Left Sidebar Navigation -->
    <MemberPortalNavigation />

    <!-- Main Content -->
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Claims</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/member-dashboard">Dashboard</router-link></li>
                    <li class="breadcrumb-item active">Claims</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card stat-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="stat-icon bg-soft-primary">
                        <i class="bx bx-file text-primary"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted mb-1 fw-medium">Total Claims</p>
                      <h4 class="mb-0">{{ totalClaimsCount }}</h4>
                      <small class="text-muted">All time</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card stat-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="stat-icon bg-soft-success">
                        <i class="bx bx-check-circle text-success"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted mb-1 fw-medium">Approved</p>
                      <h4 class="mb-0">{{ approvedCount }}</h4>
                      <small class="text-success">ETB {{ formatCurrency(approvedAmount) }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card stat-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="stat-icon bg-soft-warning">
                        <i class="bx bx-time text-warning"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted mb-1 fw-medium">Pending</p>
                      <h4 class="mb-0">{{ pendingCount }}</h4>
                      <small class="text-warning">Awaiting review</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card stat-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="stat-icon bg-soft-danger">
                        <i class="bx bx-x-circle text-danger"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted mb-1 fw-medium">Rejected</p>
                      <h4 class="mb-0">{{ rejectedCount }}</h4>
                      <small class="text-danger">Not approved</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filter Bar -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card">
                <div class="card-body py-3">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <div class="d-flex flex-wrap align-items-center gap-2">
                        <div class="search-box">
                          <div class="position-relative">
                            <input type="text" class="form-control" placeholder="Search claims..." v-model="searchQuery">
                            <i class="bx bx-search-alt search-icon"></i>
                          </div>
                        </div>
                        <select class="form-select" style="width: 140px;" v-model="filterStatus">
                          <option value="">All Status</option>
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                          <option value="processing">Processing</option>
                          <option value="paid">Paid</option>
                        </select>
                        <select class="form-select" style="width: 160px;" v-model="filterType">
                          <option value="">All Types</option>
                          <option value="outpatient">Outpatient</option>
                          <option value="inpatient">Inpatient</option>
                          <option value="emergency">Emergency</option>
                          <option value="pharmacy">Pharmacy</option>
                          <option value="laboratory">Laboratory</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4 text-md-end mt-3 mt-md-0">
                      <span class="text-muted small">
                        Showing {{ filteredClaims.length }} of {{ claims.length }} claims
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading claims...</p>
          </div>

          <!-- Claims Content -->
          <div v-else>
            <!-- Claims Table (Desktop) -->
            <div class="card d-none d-md-block" v-if="filteredClaims.length > 0">
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead class="table-light">
                    <tr>
                      <th>Claim ID</th>
                      <th>Date</th>
                      <th>Facility</th>
                      <th>Type</th>
                      <th class="text-end">Amount</th>
                      <th class="text-center">Status</th>
                      <th class="text-center">Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="claim in paginatedClaims" :key="claim.claim_id" class="claim-row">
                      <td>
                        <span class="claim-id">{{ claim.claim_number }}</span>
                      </td>
                      <td>
                        <div>
                          <span class="d-block">{{ formatDate(claim.service_date || claim.claim_date) }}</span>
                          <small class="text-muted">{{ formatTime(claim.created_at) }}</small>
                        </div>
                      </td>
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="facility-icon me-2">
                            <i class="bx bx-building-house"></i>
                          </div>
                          <div>
                            <span class="d-block">{{ claim.facility_name || 'N/A' }}</span>
                            <small class="text-muted">{{ claim.facility_type || '' }}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span class="badge" :class="getTypeBadgeClass(claim.claim_type)">
                          <i :class="getTypeIcon(claim.claim_type)" class="me-1"></i>
                          {{ getClaimTypeDisplay(claim.claim_type) }}
                        </span>
                      </td>
                      <td class="text-end">
                        <div>
                          <strong>ETB {{ formatCurrency(claim.claimed_amount) }}</strong>
                          <div v-if="claim.approved_amount && (claim.status === 'approved' || claim.status === 'paid')" class="small text-success">
                            Approved: ETB {{ formatCurrency(claim.approved_amount) }}
                          </div>
                        </div>
                      </td>
                      <td class="text-center">
                        <span class="badge" :class="getStatusBadgeClass(claim.status)">
                          <i :class="getStatusIcon(claim.status)" class="me-1"></i>
                          {{ capitalizeFirst(claim.status) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <button class="btn btn-soft-primary btn-sm" @click="viewClaim(claim)" title="View Details">
                          <i class="bx bx-show"></i>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="card-footer bg-white border-top" v-if="totalPages > 1">
                  <div class="d-flex justify-content-between align-items-center">
                    <small class="text-muted">
                      Showing {{ paginationStart }} to {{ paginationEnd }} of {{ filteredClaims.length }} entries
                    </small>
                    <nav>
                      <ul class="pagination pagination-sm mb-0">
                        <li class="page-item" :class="{ disabled: currentPage === 1 }">
                          <a class="page-link" href="#" @click.prevent="currentPage--">
                            <i class="bx bx-chevron-left"></i>
                          </a>
                        </li>
                        <li class="page-item" v-for="page in displayedPages" :key="page"
                            :class="{ active: currentPage === page }">
                          <a class="page-link" href="#" @click.prevent="currentPage = page">{{ page }}</a>
                        </li>
                        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                          <a class="page-link" href="#" @click.prevent="currentPage++">
                            <i class="bx bx-chevron-right"></i>
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>

            <!-- Claims Cards (Mobile) -->
            <div class="d-md-none" v-if="filteredClaims.length > 0">
              <div class="card claim-card mb-2" v-for="claim in paginatedClaims" :key="'mobile-' + claim.claim_id">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <span class="claim-id d-block">{{ claim.claim_number }}</span>
                      <small class="text-muted">{{ formatDate(claim.service_date || claim.claim_date) }}</small>
                    </div>
                    <span class="badge" :class="getStatusBadgeClass(claim.status)">
                      {{ capitalizeFirst(claim.status) }}
                    </span>
                  </div>
                  <div class="mb-2">
                    <i class="bx bx-building-house text-muted me-1"></i>
                    <span class="small">{{ claim.facility_name || 'N/A' }}</span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <span class="badge" :class="getTypeBadgeClass(claim.claim_type)">
                        {{ getClaimTypeDisplay(claim.claim_type) }}
                      </span>
                    </div>
                    <div class="text-end">
                      <strong>ETB {{ formatCurrency(claim.claimed_amount) }}</strong>
                      <button class="btn btn-soft-primary btn-sm ms-2" @click="viewClaim(claim)">
                        <i class="bx bx-show"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Mobile Pagination -->
              <div class="d-flex justify-content-center mt-3" v-if="totalPages > 1">
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="currentPage--">
                        <i class="bx bx-chevron-left"></i>
                      </a>
                    </li>
                    <li class="page-item" v-for="page in displayedPages" :key="'m-' + page"
                        :class="{ active: currentPage === page }">
                      <a class="page-link" href="#" @click.prevent="currentPage = page">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="currentPage++">
                        <i class="bx bx-chevron-right"></i>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <!-- No Claims -->
            <div class="card" v-if="claims.length === 0">
              <div class="card-body text-center py-5">
                <i class="bx bx-file text-muted" style="font-size: 4rem; opacity: 0.5;"></i>
                <h5 class="text-muted mt-3">No Claims Yet</h5>
                <p class="text-muted mb-0">You haven't submitted any claims.</p>
              </div>
            </div>

            <!-- No Results from Filter -->
            <div class="card" v-else-if="filteredClaims.length === 0">
              <div class="card-body text-center py-4">
                <i class="bx bx-search-alt text-muted" style="font-size: 3rem;"></i>
                <h5 class="text-muted mt-3">No Results Found</h5>
                <p class="text-muted">No claims match your search criteria.</p>
                <button class="btn btn-outline-primary btn-sm" @click="clearFilters">
                  <i class="bx bx-refresh me-1"></i>Clear Filters
                </button>
              </div>
            </div>

            <!-- Claims Info Card -->
            <div class="row mt-4" v-if="claims.length > 0">
              <div class="col-lg-6">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-info-circle text-primary me-2"></i>Claims Information
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="info-item d-flex mb-3">
                      <div class="info-icon bg-soft-primary me-3">
                        <i class="bx bx-time text-primary"></i>
                      </div>
                      <div>
                        <h6 class="mb-1">Processing Time</h6>
                        <p class="text-muted mb-0 small">Claims are typically processed within 5-10 business days.</p>
                      </div>
                    </div>
                    <div class="info-item d-flex mb-3">
                      <div class="info-icon bg-soft-success me-3">
                        <i class="bx bx-check-shield text-success"></i>
                      </div>
                      <div>
                        <h6 class="mb-1">Coverage Verification</h6>
                        <p class="text-muted mb-0 small">Only services covered under your plan will be approved.</p>
                      </div>
                    </div>
                    <div class="info-item d-flex">
                      <div class="info-icon bg-soft-warning me-3">
                        <i class="bx bx-receipt text-warning"></i>
                      </div>
                      <div>
                        <h6 class="mb-1">Required Documents</h6>
                        <p class="text-muted mb-0 small">Keep all receipts and medical reports for claim verification.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-help-circle text-primary me-2"></i>Need Help?
                    </h5>
                  </div>
                  <div class="card-body">
                    <p class="text-muted">If you have questions about your claims or need assistance, please contact your local CBHI office or call our support line.</p>
                    <div class="d-flex flex-wrap gap-2">
                      <div class="contact-item">
                        <i class="bx bx-phone text-primary me-1"></i>
                        <span>Hotline: 8335</span>
                      </div>
                      <div class="contact-item">
                        <i class="bx bx-envelope text-primary me-1"></i>
                        <span>support@moh.gov.et</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>

  <!-- Claim Details Modal -->
  <div class="modal-overlay" v-if="showClaimModal" @click.self="closeClaimModal">
    <div class="modal-container modal-lg">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header claim-modal-header">
          <h5 class="modal-title text-white">
            <i class="bx bx-file me-2"></i>Claim Details
          </h5>
          <button type="button" class="btn-close-white" @click="closeClaimModal">&times;</button>
        </div>

        <!-- Claim content -->
        <div v-if="selectedClaim" class="claim-details-container">
          <!-- Claim Header -->
          <div class="claim-header">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <h5 class="claim-number mb-1">{{ selectedClaim.claim_number }}</h5>
                <p class="text-muted mb-0">Submitted on {{ formatDate(selectedClaim.created_at) }}</p>
              </div>
              <span class="badge badge-lg" :class="getStatusBadgeClass(selectedClaim.status)">
                <i :class="getStatusIcon(selectedClaim.status)" class="me-1"></i>
                {{ capitalizeFirst(selectedClaim.status) }}
              </span>
            </div>
          </div>

          <!-- Claim Body -->
          <div class="claim-body">
            <!-- Service Info -->
            <div class="section mb-4">
              <h6 class="section-title">
                <i class="bx bx-plus-medical me-2"></i>Service Information
              </h6>
              <div class="row">
                <div class="col-md-6">
                  <div class="info-row">
                    <span class="label">Claim Date</span>
                    <span class="value">{{ formatDate(selectedClaim.claim_date) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Service Date</span>
                    <span class="value">{{ formatDate(selectedClaim.service_date) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Claim Type</span>
                    <span class="badge" :class="getTypeBadgeClass(selectedClaim.claim_type)">
                      {{ getClaimTypeDisplay(selectedClaim.claim_type) }}
                    </span>
                  </div>
                  <div class="info-row" v-if="hasValue(selectedClaim.diagnosis)">
                    <span class="label">Diagnosis</span>
                    <span class="value">{{ selectedClaim.diagnosis }}</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="info-row">
                    <span class="label">Facility</span>
                    <span class="value">{{ selectedClaim.facility_name || 'N/A' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Facility Type</span>
                    <span class="value">{{ selectedClaim.facility_type || 'N/A' }}</span>
                  </div>
                  <div class="info-row" v-if="hasValue(selectedClaim.doctor_name)">
                    <span class="label">Treating Doctor</span>
                    <span class="value">{{ selectedClaim.doctor_name }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Treatment Description -->
            <div class="section mb-4" v-if="hasValue(selectedClaim.treatment_description)">
              <h6 class="section-title">
                <i class="bx bx-detail me-2"></i>Treatment Details
              </h6>
              <div class="treatment-box">
                {{ selectedClaim.treatment_description }}
              </div>
            </div>

            <!-- Amount Info -->
            <div class="section mb-4">
              <h6 class="section-title">
                <i class="bx bx-money me-2"></i>Amount Details
              </h6>
              <div class="amount-cards">
                <div class="amount-card">
                  <span class="amount-label">Claimed Amount</span>
                  <span class="amount-value">ETB {{ formatCurrency(selectedClaim.claimed_amount) }}</span>
                </div>
                <div class="amount-card" v-if="selectedClaim.status === 'approved' || selectedClaim.status === 'paid'">
                  <span class="amount-label">Approved Amount</span>
                  <span class="amount-value text-success">ETB {{ formatCurrency(selectedClaim.approved_amount) }}</span>
                </div>
                <div class="amount-card" v-if="hasValue(selectedClaim.patient_contribution) && parseFloat(selectedClaim.patient_contribution) > 0">
                  <span class="amount-label">Patient Contribution</span>
                  <span class="amount-value text-info">ETB {{ formatCurrency(selectedClaim.patient_contribution) }}</span>
                </div>
                <div class="amount-card" v-if="(selectedClaim.status === 'approved' || selectedClaim.status === 'paid') && parseFloat(selectedClaim.claimed_amount) !== parseFloat(selectedClaim.approved_amount)">
                  <span class="amount-label">Difference</span>
                  <span class="amount-value text-warning">ETB {{ formatCurrency(parseFloat(selectedClaim.claimed_amount) - parseFloat(selectedClaim.approved_amount)) }}</span>
                </div>
              </div>
            </div>

            <!-- Beneficiary Info -->
            <div class="section mb-4" v-if="hasValue(selectedClaim.beneficiary_name)">
              <h6 class="section-title">
                <i class="bx bx-user me-2"></i>Beneficiary
              </h6>
              <div class="info-row">
                <span class="label">Name</span>
                <span class="value">{{ selectedClaim.beneficiary_name }}</span>
              </div>
              <div class="info-row" v-if="hasValue(selectedClaim.beneficiary_relationship)">
                <span class="label">Relationship</span>
                <span class="value">{{ capitalizeFirst(selectedClaim.beneficiary_relationship) }}</span>
              </div>
            </div>

            <!-- Approval Info -->
            <div class="section mb-4" v-if="(selectedClaim.status === 'approved' || selectedClaim.status === 'paid')">
              <h6 class="section-title text-success">
                <i class="bx bx-check-circle me-2"></i>Approval Information
              </h6>
              <div class="alert alert-success mb-0">
                <div class="d-flex flex-column">
                  <div v-if="hasValue(selectedClaim.approval_reason)">
                    <strong>Reason:</strong> {{ selectedClaim.approval_reason }}
                  </div>
                  <div v-if="hasValue(selectedClaim.approved_by)" class="mt-1">
                    <small class="text-muted">Approved by: {{ selectedClaim.approved_by }} on {{ formatDate(selectedClaim.approval_date) }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Status Timeline -->
            <div class="section mb-4">
              <h6 class="section-title">
                <i class="bx bx-history me-2"></i>Status History
              </h6>
              <div class="timeline">
                <div class="timeline-item">
                  <div class="timeline-dot bg-primary"></div>
                  <div class="timeline-content">
                    <span class="timeline-title">Claim Submitted</span>
                    <span class="timeline-date">{{ formatDateTime(selectedClaim.created_at) }}</span>
                  </div>
                </div>
                <div class="timeline-item" v-if="selectedClaim.status !== 'pending'">
                  <div class="timeline-dot" :class="getTimelineDotClass(selectedClaim.status)"></div>
                  <div class="timeline-content">
                    <span class="timeline-title">{{ capitalizeFirst(selectedClaim.status) }}</span>
                    <span class="timeline-date">{{ formatDateTime(selectedClaim.updated_at) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Rejection Reason -->
            <div class="section mb-4" v-if="selectedClaim.status === 'rejected' && hasValue(selectedClaim.rejection_reason)">
              <h6 class="section-title text-danger">
                <i class="bx bx-x-circle me-2"></i>Rejection Reason
              </h6>
              <div class="alert alert-danger mb-0">
                {{ selectedClaim.rejection_reason }}
              </div>
            </div>

            <!-- Notes -->
            <div class="section" v-if="hasValue(selectedClaim.notes)">
              <h6 class="section-title">
                <i class="bx bx-note me-2"></i>Notes
              </h6>
              <p class="notes-text">{{ selectedClaim.notes }}</p>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeClaimModal">
            <i class="bx bx-x me-1"></i>Close
          </button>
        </div>
      </div>
    </div>
  </div>

  </body>
</template>

<script>
/* eslint-disable */
import MemberPortalNavigation from "@/components/member-portal/MemberPortalNavigation.vue";
import { memberUrl, execute } from "@/api";

export default {
  name: "ClaimsList",
  components: {
    MemberPortalNavigation
  },

  data() {
    return {
      loading: false,
      claims: [],

      // API Summary data
      apiSummary: {
        total_claims: 0,
        approved: 0,
        pending: 0,
        rejected: 0,
        paid: 0,
        total_claimed_amount: 0,
        total_approved_amount: 0
      },

      // Filters
      searchQuery: '',
      filterStatus: '',
      filterType: '',

      // Pagination
      currentPage: 1,
      perPage: 10,

      // Modal
      showClaimModal: false,
      selectedClaim: null
    };
  },

  computed: {
    totalClaimsCount() {
      return parseInt(this.apiSummary.total_claims) || this.claims.length;
    },

    approvedCount() {
      return parseInt(this.apiSummary.approved) || this.claims.filter(c => c.status === 'approved').length;
    },

    pendingCount() {
      return parseInt(this.apiSummary.pending) || this.claims.filter(c => c.status === 'pending').length;
    },

    rejectedCount() {
      return parseInt(this.apiSummary.rejected) || this.claims.filter(c => c.status === 'rejected').length;
    },

    approvedAmount() {
      return parseFloat(this.apiSummary.total_approved_amount) || this.claims
          .filter(c => c.status === 'approved' || c.status === 'paid')
          .reduce((sum, c) => sum + (parseFloat(c.approved_amount) || 0), 0);
    },

    filteredClaims() {
      let result = [...this.claims];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(c =>
            (c.claim_number && c.claim_number.toLowerCase().includes(query)) ||
            (c.facility_name && c.facility_name.toLowerCase().includes(query)) ||
            (c.diagnosis && c.diagnosis.toLowerCase().includes(query)) ||
            (c.treatment_description && c.treatment_description.toLowerCase().includes(query))
        );
      }

      // Status filter
      if (this.filterStatus) {
        result = result.filter(c => c.status === this.filterStatus);
      }

      // Type filter
      if (this.filterType) {
        result = result.filter(c => c.claim_type && c.claim_type.toLowerCase() === this.filterType.toLowerCase());
      }

      // Sort by date descending
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      return result;
    },

    totalPages() {
      return Math.ceil(this.filteredClaims.length / this.perPage);
    },

    paginatedClaims() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredClaims.slice(start, end);
    },

    paginationStart() {
      if (this.filteredClaims.length === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.filteredClaims.length);
    },

    displayedPages() {
      const pages = [];
      const maxPages = 5;
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + maxPages - 1);

      if (end - start < maxPages - 1) {
        start = Math.max(1, end - maxPages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    filterStatus() {
      this.currentPage = 1;
    },
    filterType() {
      this.currentPage = 1;
    }
  },

  mounted() {
    this.loadClaims();
  },

  methods: {
    async loadClaims() {
      this.loading = true;

      try {
        const data = {
          function: "getMyClaims"
        };

        const res = await execute(data, memberUrl);

        if (res.data.success) {
          // Get claims from either key
          this.claims = res.data.data.claims || res.data.data.Records || [];

          // Store API summary if available
          if (res.data.data.summary) {
            this.apiSummary = res.data.data.summary;
          }
        }
      } catch (error) {
        console.error('Error loading claims:', error);
      } finally {
        this.loading = false;
      }
    },

    viewClaim(claim) {
      this.selectedClaim = claim;
      this.showClaimModal = true;
    },

    closeClaimModal() {
      this.showClaimModal = false;
      this.selectedClaim = null;
    },

    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = '';
      this.filterType = '';
    },

    // Helper to check if value exists and is not empty
    hasValue(val) {
      return val !== null && val !== undefined && val !== '';
    },

    // Get claim type display text
    getClaimTypeDisplay(type) {
      if (!type) return 'General';
      return this.capitalizeFirst(type);
    },

    getStatusBadgeClass(status) {
      const classes = {
        'approved': 'bg-success',
        'pending': 'bg-warning text-dark',
        'processing': 'bg-info',
        'rejected': 'bg-danger',
        'cancelled': 'bg-secondary',
        'paid': 'bg-primary'
      };
      return classes[status?.toLowerCase()] || 'bg-secondary';
    },

    getStatusIcon(status) {
      const icons = {
        'approved': 'bx bx-check',
        'pending': 'bx bx-time',
        'processing': 'bx bx-loader-alt',
        'rejected': 'bx bx-x',
        'cancelled': 'bx bx-block',
        'paid': 'bx bx-check-double'
      };
      return icons[status?.toLowerCase()] || 'bx bx-help-circle';
    },

    getTimelineDotClass(status) {
      const classes = {
        'approved': 'bg-success',
        'paid': 'bg-primary',
        'rejected': 'bg-danger',
        'processing': 'bg-info',
        'cancelled': 'bg-secondary'
      };
      return classes[status?.toLowerCase()] || 'bg-warning';
    },

    getTypeBadgeClass(type) {
      if (!type) return 'bg-soft-secondary text-secondary';

      const classes = {
        'outpatient': 'bg-soft-primary text-primary',
        'inpatient': 'bg-soft-info text-info',
        'emergency': 'bg-soft-danger text-danger',
        'pharmacy': 'bg-soft-success text-success',
        'laboratory': 'bg-soft-warning text-warning'
      };
      return classes[type.toLowerCase()] || 'bg-soft-secondary text-secondary';
    },

    getTypeIcon(type) {
      if (!type) return 'bx bx-file';

      const icons = {
        'outpatient': 'bx bx-walk',
        'inpatient': 'bx bx-bed',
        'emergency': 'bx bx-plus-medical',
        'pharmacy': 'bx bx-capsule',
        'laboratory': 'bx bx-test-tube'
      };
      return icons[type.toLowerCase()] || 'bx bx-file';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatCurrency(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat().format(amount);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
    }
  }
};
</script>

<style scoped>
/* Content area with sidebar */
.content-minimize {
  margin-left: 250px;
  transition: margin-left 0.3s ease;
}

.page-content {
  padding: 90px 20px 60px 20px;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.container-fluid {
  padding-left: 15px;
  padding-right: 15px;
}

@media (max-width: 992px) {
  .content-minimize {
    margin-left: 0 !important;
  }

  .page-content {
    padding: 80px 15px 60px 15px;
  }
}

/* Stat Cards */
.stat-card {
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

/* Soft Backgrounds */
.bg-soft-primary { background-color: rgba(44, 90, 160, 0.15); }
.bg-soft-success { background-color: rgba(40, 167, 69, 0.15); }
.bg-soft-info { background-color: rgba(23, 162, 184, 0.15); }
.bg-soft-warning { background-color: rgba(255, 193, 7, 0.15); }
.bg-soft-danger { background-color: rgba(220, 53, 69, 0.15); }
.bg-soft-secondary { background-color: rgba(108, 117, 125, 0.15); }

/* Search Box */
.search-box {
  position: relative;
  min-width: 200px;
}

.search-box .form-control {
  padding-left: 40px;
  border-radius: 0.5rem;
}

.search-box .search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}

/* Cards */
.card {
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e3f70;
  margin: 0;
}

/* Table */
.table {
  margin-bottom: 0;
}

.table th {
  font-weight: 600;
  font-size: 0.85rem;
  color: #495057;
  border-bottom: 2px solid #e9ecef;
}

.table td {
  vertical-align: middle;
  font-size: 0.9rem;
}

.claim-row:hover {
  background-color: #f8f9fa;
}

/* Claim ID */
.claim-id {
  font-family: monospace;
  font-size: 0.85rem;
  color: #2c5aa0;
  font-weight: 600;
}

/* Facility Icon */
.facility-icon {
  width: 36px;
  height: 36px;
  background: rgba(44, 90, 160, 0.1);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c5aa0;
}

/* Soft Buttons */
.btn-soft-primary {
  background-color: rgba(44, 90, 160, 0.1);
  color: #2c5aa0;
  border: none;
}

.btn-soft-primary:hover {
  background-color: #2c5aa0;
  color: #fff;
}

/* Mobile Claim Cards */
.claim-card {
  border-left: 4px solid #2c5aa0;
}

/* Pagination */
.pagination .page-link {
  color: #2c5aa0;
  border-color: #e9ecef;
}

.pagination .page-item.active .page-link {
  background-color: #2c5aa0;
  border-color: #2c5aa0;
}

/* Info Items */
.info-item {
  padding: 0.5rem 0;
}

.info-icon {
  width: 40px;
  height: 40px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.contact-item {
  background: #f8f9fa;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-container {
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.modal-container.modal-lg {
  max-width: 800px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e9ecef;
}

.claim-modal-header {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  border-radius: 0.75rem 0.75rem 0 0;
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e9ecef;
}

.btn-close-white {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  line-height: 1;
  opacity: 0.8;
}

.btn-close-white:hover {
  opacity: 1;
}

/* Claim Details */
.claim-details-container {
  background: white;
}

.claim-header {
  padding: 1.25rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.claim-number {
  font-family: monospace;
  font-size: 1.1rem;
  color: #2c5aa0;
  font-weight: 600;
}

.badge-lg {
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}

.claim-body {
  padding: 1.25rem;
}

.section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e3f70;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px dashed #e9ecef;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  color: #6c757d;
  font-size: 0.9rem;
}

.info-row .value {
  font-weight: 500;
  color: #212529;
}

/* Treatment Box */
.treatment-box {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.5rem;
  border-left: 4px solid #2c5aa0;
  font-size: 0.9rem;
  color: #495057;
}

/* Amount Cards */
.amount-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.amount-card {
  flex: 1;
  min-width: 150px;
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.5rem;
  text-align: center;
}

.amount-label {
  display: block;
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.amount-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
}

/* Timeline */
.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 8px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}

.timeline-item {
  position: relative;
  padding-bottom: 1rem;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -26px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 0 0 2px #e9ecef;
}

.timeline-content {
  display: flex;
  flex-direction: column;
}

.timeline-title {
  font-weight: 500;
  color: #212529;
}

.timeline-date {
  font-size: 0.8rem;
  color: #6c757d;
}

.notes-text {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.5rem;
  font-style: italic;
  color: #6c757d;
}

/* Alert styles */
.alert {
  border-radius: 0.5rem;
  padding: 1rem;
}

.alert-success {
  background-color: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.2);
  color: #155724;
}

.alert-danger {
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.2);
  color: #721c24;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    margin: 0.5rem;
    max-height: 95vh;
  }

  .claim-body {
    padding: 1rem;
  }

  .amount-cards {
    flex-direction: column;
  }

  .info-row {
    flex-direction: column;
    gap: 0.25rem;
  }

  .info-row .label {
    font-size: 0.8rem;
  }
}
</style>